<template>
  <div>
    <v-card class="car-number-search-card border">
      <v-card-title class="car-number-search-card__title">
        <v-icon left class="car-number-search-card__icon">mdi-car-search</v-icon>
        <span class="car-number-search-card__label">Поиск по гос. номеру</span>
        <v-btn
          icon
          class="car-number-search-card__close"
          @click="closeModal"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="car-number-search-card__content pa-4 pa-sm-6 pb-8">
        <template v-if="summary">
          <div class="car-number-search-card__summary mb-4">
            <div class="summary-main">
              <div class="summary-number-block">
                <span class="summary-number-label">Гос. номер</span>
                <span class="summary-number-value">{{ summary.car_number_original || summary.car_number_normalized }}</span>
              </div>
              <div class="summary-actions">
                <v-btn
                  color="grey darken-1"
                  small
                  outlined
                  class="text-none"
                  @click.exact="goToCarNumberDetail"
                  @click.ctrl.exact.prevent="goToCarNumberDetailNewTab"
                  @click.meta.exact.prevent="goToCarNumberDetailNewTab"
                  @click.middle.prevent="goToCarNumberDetailNewTab"
                >
                  <v-icon left small>mdi-information-outline</v-icon>
                  Подробнее
                </v-btn>
              </div>
            </div>
            <div class="summary-stats">
              <v-chip small class="stat-chip" outlined>
                <v-icon x-small left>mdi-format-list-bulleted</v-icon>
                Записей: {{ summary.total_in_car_numbers }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                <v-icon x-small left>mdi-history</v-icon>
                История гостей: {{ summary.total_in_guest_history }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                <v-icon x-small left>mdi-ticket-account</v-icon>
                Гост. пропуска: {{ summary.total_in_guest_passes }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                <v-icon x-small left>mdi-account-multiple</v-icon>
                Абоненты: {{ summary.total_in_abonents }}
              </v-chip>
            </div>
          </div>

          <v-data-table
            v-if="carNumbers.length"
            class="car-number-search-card__table"
            :headers="headers"
            :items="carNumbers"
            hide-default-footer
            disable-pagination
            disable-sort
          >
            <template v-slot:item.client_phone="{ item }">
              <template v-if="getMatchedAbonents(item).length">
                <div v-for="(abonent, idx) in getMatchedAbonents(item)" :key="`phone-${item.id || idx}`">
                  {{ formatPhone(abonent.telephone) }}
                </div>
              </template>
              <span v-else>—</span>
            </template>
            <template v-slot:item.client_address="{ item }">
              <template v-if="getMatchedAbonents(item).length">
                <div v-for="(abonent, idx) in getMatchedAbonents(item)" :key="`address-${item.id || idx}`">
                  {{ formatAbonentAddress(abonent) }}
                </div>
              </template>
              <span v-else>{{ item.client_id || '—' }}</span>
            </template>
            <template v-slot:item.barrier_name="{ item }">
              <template v-if="item.is_portfolio">
                <v-chip x-small color="grey lighten-2">Портфолио</v-chip>
              </template>
              <template v-else>
                {{ formatBarrierAddress(item.barrier_address || item.barrier_name) }}
              </template>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip
                x-small
                :color="item.is_active ? 'green lighten-5' : 'grey lighten-3'"
                :text-color="item.is_active ? 'green darken-3' : 'grey darken-2'"
              >
                {{ item.is_active ? 'Активен' : 'Неактивен' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    color="grey darken-1"
                    outlined
                    class="text-none"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="getClientId(item) == null"
                    @click.stop="goToClient(getClientId(item))"
                  >
                    Клиент
                  </v-btn>
                </template>
                <span>Перейти в карточку клиента</span>
              </v-tooltip>
            </template>
          </v-data-table>

          <p v-else class="grey--text text-center py-4">
            Записей по гос. номеру не найдено.
          </p>
        </template>

        <template v-else-if="error">
          <p class="error--text text-center py-4">{{ error }}</p>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'CarNumberSearchModal',
  props: {
    result: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      headers: [
        { text: 'Телефон клиента', value: 'client_phone' },
        { text: 'Адрес', value: 'client_address' },
        { text: 'Статус', value: 'is_active', width: '100' },
        { text: 'Шлагбаум', value: 'barrier_name' },
        { text: '', value: 'actions', sortable: false, width: '120', align: 'right' },
      ],
    };
  },
  computed: {
    displayResult () {
      return this.result;
    },
    summary () {
      return this.displayResult?.summary || null;
    },
    carNumbers () {
      return this.displayResult?.car_numbers || [];
    },
    abonents () {
      return this.displayResult?.abonents || [];
    },
  },
  methods: {
    formatPhone (phone) {
      if (!phone) return '—';
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length >= 11) {
        return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
      }
      return phone;
    },
    getMatchedAbonents (item) {
      const number = String(item?.car_number || this.summary?.car_number_normalized || '').trim().toUpperCase();
      if (!number) return [];
      return (this.abonents || []).filter((abonent) => {
        const cars = Array.isArray(abonent?.car_number) ? abonent.car_number : [];
        return cars.some(car => String(car || '').trim().toUpperCase() === number);
      });
    },
    getClientId (item) {
      if (item?.client_id != null) return item.client_id;
      const fromAbonent = this.getMatchedAbonents(item)
        .flatMap(abonent => (Array.isArray(abonent?.clients) ? abonent.clients : []))
        .find(client => client?.client_id != null);
      return fromAbonent?.client_id ?? null;
    },
    formatAddressWithoutCity (value) {
      if (!value) return '—';
      const source = String(value).trim();
      const normalized = source
        .replace('г.Ульяновск', '')
        .replace(/^,\s*/, '')
        .trim();
      return normalized || source;
    },
    formatAbonentAddress (abonent) {
      if (!abonent || typeof abonent !== 'object') return '—';
      const addressRaw = abonent.full_address || abonent.address || '';
      const address = this.formatAddressWithoutCity(addressRaw);
      const apartment = abonent.apartment ? `кв.${abonent.apartment}` : '';
      return [address, apartment].filter(Boolean).join(', ');
    },
    getCarNumberDetailRoute () {
      const num = this.summary?.car_number_original || this.summary?.car_number_normalized;
      if (!num) return null;
      return { name: 'CarNumberSearchDetail', query: { car_number: num } };
    },
    goToCarNumberDetail () {
      const route = this.getCarNumberDetailRoute();
      if (!route) return;
      this.closeModal();
      this.$nextTick(() => {
        this.$router.push(route);
      });
    },
    goToCarNumberDetailNewTab () {
      const route = this.getCarNumberDetailRoute();
      if (!route) return;
      const resolved = this.$router.resolve(route);
      window.open(resolved.href, '_blank');
    },
    goToClient (clientId) {
      if (clientId == null || clientId === '') return;
      this.closeModal();
      this.$nextTick(() => {
        this.$router.push({ name: 'DetailClient', params: { id: String(clientId) } });
      });
    },
    formatBarrierAddress (value) {
      if (!value) return '—';
      return this.formatAddressWithoutCity(value);
    },
    closeModal () {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.car-number-search-card {
  overflow: hidden;
  border-radius: 20px !important;
}

.car-number-search-card__title {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 16px 48px 16px 16px;
  background: linear-gradient(135deg, #eef3f8 0%, #e7eef6 55%, #dde7f2 100%);
  color: #3f5268;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.car-number-search-card__icon {
  margin-right: 10px;
  color: #5d7289;
}

.car-number-search-card__close {
  position: absolute;
  top: 50%;
  right: 8px;
  margin-top: -20px;
  color: #6a8096 !important;

  &:hover {
    color: #445a71 !important;
    background: rgba(93, 114, 137, 0.14) !important;
  }
}

.car-number-search-card__content {
  background: #fafafa;
}

.car-number-search-card__table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.car-number-search-card__summary {
  .summary-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }
  .summary-number-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .summary-number-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.5);
  }
  .summary-number-value {
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #37474f;
  }
  .summary-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .stat-chip {
    font-size: 0.8rem;
    border-color: rgba(0, 0, 0, 0.18);
    color: #455a64;
  }
}

.border {
  border-radius: 20px;
}

@media screen and (max-width: 600px) {
  .car-number-search-card__title {
    font-size: 1.1rem;
    padding-left: 12px;
  }

  .car-number-search-card__icon {
    font-size: 1.25rem !important;
  }

  .summary-stats {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
